<template>
  <div class="whatsapp-login internal-bady" :dir="isHebrew ? 'rtl' : 'ltr'">
    <NavBar/>

    <section class="wa-login-section px-3 px-md-4">
      <div class="container">
        <div class="row justify-content-center">
          <div class="col-12 col-md-8 col-lg-6">
            <div class="wa-login-card rounded-5 shadow-sm p-4 p-md-5">

              <div v-if="!failed && !pendingSwitch" class="wa-login-state text-center">
                <b-spinner variant="primary" class="wa-login-spinner mb-3"></b-spinner>
                <p class="wa-login-message mb-0">{{ t('whatsappLoginLoading') }}</p>
              </div>

              <div v-else-if="failed" class="wa-login-state wa-login-state-failed text-center">
                <span class="wa-login-failed-icon" aria-hidden="true"><i class="fa-solid fa-circle-exclamation"></i></span>
                <p class="wa-login-message wa-login-failed-text mb-0">{{ t('whatsappLoginFailed') }}</p>
              </div>

              <div v-else class="switch-choice" :class="isHebrew ? 'text-right' : 'text-left'">
                <p class="wa-login-signed-in">{{ t('whatsappLoginAlreadySignedIn', { email: currentEmail }) }}</p>
                <p class="wa-login-message">{{ t('whatsappLoginSwitchExplain') }}</p>
                <div class="switch-actions">
                  <b-button
                    type="button"
                    variant="primary"
                    size="lg"
                    block
                    class="wa-login-btn rounded-pill fw-semibold"
                    @click="openTrip"
                  >
                    {{ t('whatsappLoginOpenTrip') }}
                  </b-button>
                  <b-button
                    type="button"
                    variant="outline-secondary"
                    size="lg"
                    block
                    class="wa-login-btn rounded-pill"
                    @click="staySignedIn"
                  >
                    {{ t('whatsappLoginStaySignedIn') }}
                  </b-button>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </section>

    <PageFooterNew/>
  </div>
</template>

<script>
import { translationMixin } from '@/mixins/translationMixin';
import whatsappInviteApi from '@/api/whatsappInvite';
import NavBar from '@/components/NavBar.vue';
import PageFooterNew from '@/components/PageFooterNew.vue';

export default {
  name: 'WhatsappLogin',
  mixins: [translationMixin],
  components: {
    NavBar,
    PageFooterNew
  },
  data() {
    return {
      failed: false,
      pendingSwitch: false,
      currentEmail: '',
      newToken: null,
      newEmail: '',
      newUserName: '',
      newRole: 'user',
      rideId: null
    };
  },
  async mounted() {
    try {
      const response = await whatsappInviteApi.exchangeToken( this.$route.params.token );
      const token = response.data.token;
      this.newToken = token;
      this.rideId = response.data.ride_id;
      this.newUserName = response.data.user_name || '';
      this.newRole = response.data.role || 'user';

      let newEmail = '';
      try {
        const tokenData = JSON.parse( atob( token.split('.')[1] ) );
        newEmail = tokenData.email || '';
      } catch ( decodeError ) {
        // A malformed token must still not break the redirect below.
      }
      this.newEmail = newEmail;

      let currentEmail = '';
      const existingToken = window.localStorage.getItem( 'travel_app_token' );
      if ( existingToken ) {
        try {
          const existingTokenData = JSON.parse( atob( existingToken.split('.')[1] ) );
          currentEmail = existingTokenData.email || '';
        } catch ( decodeError ) {
          // A malformed existing token must not break the redirect below.
        }
      }

      if ( !existingToken || !currentEmail || currentEmail === newEmail ) {
        // No current session, or same person: behave exactly as before, no prompt.
        this.commitAndRedirect();
      } else {
        // Different person is currently signed in: ask before switching.
        this.currentEmail = currentEmail;
        this.pendingSwitch = true;
      }
    } catch ( error ) {
      this.failed = true;
    }
  },
  methods: {
    commitAndRedirect() {
      // Store exactly where appauth reads it from, so the rest of the app sees
      // an ordinary logged-in user. This must set every field the normal login
      // action sets (appauth.js `appLogIn`: token, role, email, userName) --
      // each one is persisted to localStorage separately, so any field left
      // uncommitted keeps the PREVIOUS account's value rather than clearing.
      // Skipping userName left the old user's initial in the navbar avatar, and
      // skipping role left a former manager's menu items visible to a traveller.
      this.$store.commit( 'setUserInformation', { token: this.newToken, role: this.newRole } );
      this.$store.commit( 'setEmail', this.newEmail );
      this.$store.commit( 'setUserName', this.newUserName );
      this.$router.replace( `/single_ride_with_map/${this.rideId}` );
    },
    openTrip() {
      this.commitAndRedirect();
    },
    staySignedIn() {
      this.$router.replace( '/' );
    }
  }
};
</script>

<style scoped>
/* Full-height column so the footer is pushed to the bottom of the viewport and
   the content area absorbs whatever is left over. This replaces a
   calc(100vh - 320px) guess at the combined NavBar + footer height, which left
   a ~300px blank band below the footer on a phone-sized viewport. Measuring
   with flex beats estimating, and it cannot drift if either component's
   height changes. */
.whatsapp-login {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

.internal-bady {
  line-height: 1.5;
}

.wa-login-section {
  padding-top: 120px;
  padding-bottom: 4rem;
  flex: 1 0 auto;
  display: flex;
  align-items: center;
}

.wa-login-card {
  background: #fff;
  width: 100%;
}

.wa-login-message {
  font-size: 1.2rem;
  line-height: 1.6;
  color: #1e1e1e;
  margin-bottom: 1rem;
}

.wa-login-message:last-child {
  margin-bottom: 0;
}

.wa-login-signed-in {
  font-size: 1.45rem;
  font-weight: 700;
  color: #0D2F29;
  margin-bottom: 0.75rem;
}

.wa-login-failed-icon {
  display: inline-flex;
  font-size: 3rem;
  color: #DD6E42;
  margin-bottom: 1rem;
}

.wa-login-failed-text {
  color: #a34e26;
}

.wa-login-spinner {
  width: 2.5rem;
  height: 2.5rem;
}

.switch-actions {
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
  margin-top: 1.75rem;
}

.wa-login-btn {
  font-size: 1.15rem;
  padding-top: 0.85rem;
  padding-bottom: 0.85rem;
}

@media (max-width: 575.98px) {
  .wa-login-section {
    padding-top: 96px;
    padding-bottom: 2.5rem;
  }
}
</style>
